/**
 * Screen 6 — Config editing modal. Same field set as the enrollment accordion
 * plus schedule cron overrides and "force full run on next fire". Loading the
 * current effective config is a nice-to-have — the API doesn't expose a
 * "read current config" endpoint (only PATCH), so we fall back to the SDK
 * defaults for the form's initial state.
 */
import { useState } from 'react';
import { toast } from 'sonner';
import { Settings2 } from 'lucide-react';
import { FormDialog } from '@/components/ui/form-dialog';
import { summarizeApiError } from '@/lib/api';
import {
  ConfigForm,
  toOverridePayload,
  type ConfigFormValues,
} from './config-form';
import { usePatchAutoCampaignConfig } from '@/features/auto-campaign/mutations';

export interface ConfigDialogProps {
  projectId: string;
  open: boolean;
  onOpenChange: (v: boolean) => void;
}

export function ConfigDialog({
  projectId,
  open,
  onOpenChange,
}: ConfigDialogProps): JSX.Element {
  const patch = usePatchAutoCampaignConfig(projectId);
  const [draft, setDraft] = useState<ConfigFormValues | null>(null);

  async function submit(e: React.FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    if (!draft) {
      onOpenChange(false);
      return;
    }
    try {
      await patch.mutateAsync(toOverridePayload(draft, true, true));
      toast.success('Auto-Campaign config saved');
      onOpenChange(false);
    } catch (err) {
      const s = summarizeApiError(err, {
        fallbackTitle: 'Could not save config',
      });
      toast.error(s.title, { description: s.description });
    }
  }

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      icon={<Settings2 className="h-5 w-5 text-primary" />}
      title="Auto-Campaign config"
      description="Adjust cadence, brief volume, and off-page platforms. Changes take effect on the next fire (they don't affect fires in-flight)."
      submitLabel="Save changes"
      submitting={patch.isPending}
      onSubmit={submit}
      size="lg"
    >
      <ConfigForm
        onChange={setDraft}
        showForceFullRun
        showSchedule
      />
    </FormDialog>
  );
}
